import React, { useRef, useState } from "react";

import { Button } from "./button";
import { BasicTooltip } from "./Tooltip";

interface ActionProps {
  className?: string;
  onClick: () => void;
  children: React.ReactNode;
}

const Action: React.FC<ActionProps> = ({ className, onClick, children }) => (
  <button
    className={`${className} rounded-md bg-white p-1 shadow-lg transition-opacity duration-200 ease-in-out`}
    onClick={onClick}
  >
    {children}
  </button>
);

const CalloutActions: React.FC<{
  actions: React.ReactNode[];
  copy: boolean;
  contentRef: React.RefObject<HTMLDivElement>;
}> = ({ actions, copy, contentRef }) => {
  const [isCopied, setIsCopied] = useState(false);
  const hasActions = actions.length > 0;

  const handleCopy = async () => {
    const text = contentRef.current?.textContent || "";
    await navigator.clipboard.writeText(text);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div
      className={`absolute right-4 top-2 flex space-x-2 rounded-md bg-opacity-90 p-1 ${
        hasActions ? "bg-white shadow-sm" : "bg-transparent"
      } opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100`}
    >
      {copy && (
        <BasicTooltip content={isCopied ? "Copied!" : "Copy"}>
          <Button
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleCopy();
            }}
            type="button"
            variant="ghost"
            size="tinyIcon"
            className="group relative text-gray-600 hover:text-gray-800 focus:outline-none"
            aria-label="Copy to clipboard"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isCopied ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              )}
            </svg>
          </Button>
        </BasicTooltip>
      )}
      {actions.map((action, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>{action}</React.Fragment>
      ))}
    </div>
  );
};

interface CalloutProps {
  children: string | React.ReactNode;
  className?: string;
  copy?: boolean;
  actions?: React.ReactNode[];
  fullHeight?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const Callout: React.FC<CalloutProps> = ({
  children,
  copy = true,
  className,
  actions = [],
  fullHeight = false,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div className="group pointer-events-auto relative">
      <CalloutActions copy={copy} contentRef={ref} actions={actions} />
      <div
        className={`${className} mb-4 mt-2 ${
          fullHeight ? "h-full" : "max-h-[250px] min-h-[55px] overflow-y-auto"
        } rounded-md border bg-gray-50 px-2 py-4 transition-all duration-200 ease-in-out group-hover:border-gray-300`}
        {...props}
        ref={ref}
      >
        <div className="flex">
          <div className="ml-3 text-sm font-medium text-blue-800">
            <p className="inline">{children}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CalloutWithTitleProps extends CalloutProps {
  title: string;
  titleSize?: "text-sm" | "text-base" | "text-lg" | "text-xl" | "text-2xl";
}

const CalloutWithTitle: React.FC<CalloutWithTitleProps> = ({
  children,
  title,
  titleSize = "text-lg",
  ...props
}) => (
  <>
    <h4 className={`mb-2 font-semibold text-gray-700 ${titleSize}`}>{title}</h4>
    <Callout {...props}>{children}</Callout>
  </>
);

export { Action, CalloutWithTitle };

export default Callout;
